import { api } from "@/services/api";

export type OAuthClientKind = "static" | "cimd" | "dcr";
export type OAuthClientResource = "api" | "mcp";
export type OAuthConsentDecision = "approve" | "deny";
export type OAuthGrantScope = "mine" | "organization";
export type OAuthScope =
  | "spai:agents:read"
  | "spai:agents:write"
  | "spai:agents:execute"
  | "spai:models:read"
  | "spai:secrets:read"
  | "spai:secrets:write"
  | "spai:channels:read"
  | "spai:channels:write"
  | "spai:threads:read"
  | "spai:threads:write"
  | "spai:analytics:read"
  | "spai:llm:execute"
  | "spai:tts:execute"
  | "spai:embeddings:execute"
  | "spai:decisions:execute"
  | "spai:stt:execute"
  | "offline_access";

export interface OAuthProjectChoice {
  id: string;
  name: string;
}

export interface OAuthOrganizationChoice {
  id: string;
  name: string;
  projects: OAuthProjectChoice[];
  allowed_scopes: OAuthScope[];
}

export interface OAuthConsentResponse {
  client: {
    id: string;
    name: string;
    kind: OAuthClientKind;
  };
  resource: string;
  resource_type: OAuthClientResource;
  scopes: OAuthScope[];
  expires_at: string;
  organizations: OAuthOrganizationChoice[];
}

export interface OAuthConsentBody {
  decision: OAuthConsentDecision;
  organization_id?: string;
  project_id?: string;
  scopes?: OAuthScope[];
}

export interface OAuthConsentResult {
  redirect_uri: string;
}

export interface OAuthDeviceConsentBody extends OAuthConsentBody {
  user_code: string;
}

export interface OAuthDeviceConsentResult {
  decision: "approved" | "denied";
}

export interface OAuthGrantItem {
  id: string;
  client_id: string;
  client_name: string;
  client_kind: OAuthClientKind;
  resource?: string;
  resource_type?: OAuthClientResource;
  user_id: string;
  user_name: string | null;
  organization_id: string;
  organization_name: string;
  project_id: string;
  project_name: string;
  scopes: OAuthScope[];
  created_at: string;
  last_used_at: string | null;
  expires_at: string;
  revoked_at: string | null;
  last_used_ip: string | null;
  can_revoke: boolean;
}

export interface OAuthGrantsResponse {
  data: OAuthGrantItem[];
  has_more: boolean;
  total: number;
}

export interface OAuthGrantsParams {
  scope: OAuthGrantScope;
  organization_id?: string;
  limit: number;
  offset: number;
}

export const oauthApi = {
  async getConsent(requestId: string): Promise<OAuthConsentResponse> {
    const { data } = await api.get<OAuthConsentResponse>(
      `/oauth/consent/${encodeURIComponent(requestId)}`,
    );
    return data;
  },

  async submitConsent(requestId: string, body: OAuthConsentBody): Promise<OAuthConsentResult> {
    const { data } = await api.post<OAuthConsentResult>(
      `/oauth/consent/${encodeURIComponent(requestId)}`,
      body,
    );
    return data;
  },

  async getDeviceConsent(userCode: string): Promise<OAuthConsentResponse> {
    const { data } = await api.get<OAuthConsentResponse>("/oauth/device/consent", {
      params: { user_code: userCode },
    });
    return data;
  },

  async submitDeviceConsent(body: OAuthDeviceConsentBody): Promise<OAuthDeviceConsentResult> {
    const { data } = await api.post<OAuthDeviceConsentResult>("/oauth/device/consent", body);
    return data;
  },

  async getGrants(params: OAuthGrantsParams): Promise<OAuthGrantsResponse> {
    const { data } = await api.get<OAuthGrantsResponse>("/oauth/grants", { params });
    return data;
  },

  async revokeGrant(id: string): Promise<void> {
    await api.delete(`/oauth/grants/${encodeURIComponent(id)}`);
  },
};
